import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ProviderCard } from '../../components/account/ProviderCard';
import { LinkProviderButton } from '../../components/account/LinkProviderButton';
import { LinkedProvidersData, OAuthProviderType } from '../../types/account.types';

const API_URL = import.meta.env.VITE_API_URL || '';

type PageState = 'loading' | 'loaded' | 'error';

/** Get auth headers with JWT token from localStorage */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface ConfirmDialogState {
  isOpen: boolean;
  providerId: string;
  providerName: string;
}

// Confirmation dialog component
function ConfirmDialog({
  isOpen,
  providerName,
  onConfirm,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  providerName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onCancel}
          aria-hidden="true"
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unlink Account?</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to unlink your {providerName} account? You will no longer be able
            to sign in with this provider.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Unlinking...
                </>
              ) : (
                'Unlink Account'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConnectedAccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<LinkedProvidersData | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    providerId: '',
    providerName: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchLinkedProviders = useCallback(async () => {
    try {
      setPageState('loading');
      const response = await fetch(`${API_URL}/api/account/providers`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch linked providers');
      }
      const result = await response.json();
      setData(result.data);
      setPageState('loaded');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      setPageState('error');
    }
  }, []);

  useEffect(() => {
    fetchLinkedProviders();

    // Check for link status from OAuth callback
    const linkStatus = searchParams.get('link');
    if (linkStatus === 'success') {
      setSuccessMessage('Account linked successfully!');
      setSearchParams({});
      fetchLinkedProviders();
    } else if (linkStatus === 'error') {
      const message = searchParams.get('message') || 'Failed to link account';
      setErrorMessage(message);
      setSearchParams({});
    }
  }, [fetchLinkedProviders, searchParams, setSearchParams]);

  const getProviderName = (provider: OAuthProviderType): string => {
    switch (provider) {
      case 'GOOGLE':
        return 'Google';
      case 'FACEBOOK':
        return 'Facebook';
      case 'EMAIL_PASSWORD':
        return 'Email & Password';
      default:
        return provider;
    }
  };

  const handleUnlinkClick = (providerId: string, provider: OAuthProviderType) => {
    setConfirmDialog({
      isOpen: true,
      providerId,
      providerName: getProviderName(provider),
    });
  };

  const handleUnlinkConfirm = async () => {
    setIsProcessing(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/account/providers/${confirmDialog.providerId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (result.success) {
        setSuccessMessage(`${confirmDialog.providerName} account unlinked successfully`);
        setConfirmDialog({ isOpen: false, providerId: '', providerName: '' });
        await fetchLinkedProviders();
      } else {
        setErrorMessage(result.message || 'Failed to unlink account');
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to unlink provider');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnlinkCancel = () => {
    setConfirmDialog({ isOpen: false, providerId: '', providerName: '' });
  };

  const handleSetPrimary = async (provider: OAuthProviderType) => {
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/account/providers/${provider}/primary`, {
        method: 'PATCH',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (result.success) {
        setSuccessMessage(`${getProviderName(provider)} is now your primary login method`);
        await fetchLinkedProviders();
      } else {
        setErrorMessage(result.message || 'Failed to set primary provider');
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to set primary provider');
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading connected accounts...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error' && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full p-8 bg-white rounded-lg shadow text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Accounts</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={fetchLinkedProviders}
              className="w-full px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
            <Link
              to="/dashboard"
              className="w-full px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-center"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const linkedProviderTypes = data.providers.map((p) => p.provider);
  const availableToLink: OAuthProviderType[] = (
    ['GOOGLE', 'FACEBOOK'] as OAuthProviderType[]
  ).filter((p) => !linkedProviderTypes.includes(p));

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Connected Accounts</h1>
          <p className="text-gray-600 mt-1">
            Manage your authentication methods and linked accounts.
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-green-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-800">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Warning for single auth method */}
        {!data.hasPassword && data.providers.length === 1 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex">
              <svg
                className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p className="text-sm text-yellow-800 font-medium">
                  You only have one authentication method
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Consider setting a password or linking another account for backup access.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Linked Accounts */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Linked Accounts ({data.providers.length})
          </h2>
          <div className="space-y-4">
            {data.providers.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                canUnlink={data.providers.length > 1 || data.hasPassword}
                onUnlink={() => handleUnlinkClick(provider.id, provider.provider)}
                onSetPrimary={() => handleSetPrimary(provider.provider)}
              />
            ))}
          </div>
        </div>

        {/* Available to Link */}
        {availableToLink.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Link Additional Accounts</h2>
            <p className="text-gray-600 text-sm mb-4">
              Connect more accounts to have multiple ways to sign in.
            </p>
            <div className="space-y-3">
              {availableToLink.map((provider) => (
                <LinkProviderButton key={provider} provider={provider} />
              ))}
            </div>
          </div>
        )}

        {/* No providers available to link */}
        {availableToLink.length === 0 && (
          <div className="text-center py-6 bg-gray-100 rounded-lg">
            <svg
              className="w-12 h-12 text-gray-400 mx-auto mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-gray-600">All available accounts are already linked.</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        providerName={confirmDialog.providerName}
        onConfirm={handleUnlinkConfirm}
        onCancel={handleUnlinkCancel}
        isLoading={isProcessing}
      />
    </div>
  );
}
